import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useBuilding } from '../contexts/BuildingContext';
import { LuUser, LuMail, LuBuilding2, LuMapPin, LuShield, LuCalendar, LuPencil, LuCheck, LuX, LuLock, LuEye, LuEyeOff, LuPhone } from 'react-icons/lu';
import { getRoleLabel } from '../utils/roleCheck';
import { toast } from 'sonner';

export default function Profile() {
  const { t } = useTranslation();
  const { user, updateUserProfile, changePassword } = useAuth();
  const { currentBuilding, currentProperty, availableBuildings, availableProperties, refresh } = useBuilding();

  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    property_id: '',
    building_id: '',
    phone_number: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        unit: user.unit || '',
        property_id: user.property_id || '',
        building_id: user.building_id || '',
        phone_number: user.phone_number || ''
      });
    }
  }, [user]);

  const handleSave = async () => {
    if (!formData.name.trim()) return;

    // Validate phone number format if provided
    if (formData.phone_number && formData.phone_number.trim()) {
      const phoneRegex = /^[\d\s\-+()]+$/;
      if (!phoneRegex.test(formData.phone_number.trim())) {
        toast.error('Please enter a valid phone number');
        return;
      }
    }

    setSaving(true);
    try {
      const updatedUser = await updateUserProfile({
        name: formData.name,
        unit: formData.unit,
        property_id: formData.property_id,
        building_id: formData.building_id,
        phone_number: formData.phone_number
      });

      // Pass the updated user directly to refresh to avoid stale state issues
      await refresh(updatedUser);
      setIsEditing(false);
    } catch (error) {




      // Error handled in context
    } finally {setSaving(false);}};
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setSaving(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
    } catch (error) {




      // Error handled in context
    } finally {setSaving(false);}};
  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      unit: user?.unit || '',
      property_id: user?.property_id || '',
      building_id: user?.building_id || '',
      phone_number: user?.phone_number || ''
    });
    setIsEditing(false);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white relative overflow-hidden shadow-lg">
        <div className="absolute top-0 right-0 p-8 opacity-10">
          <LuUser className="w-64 h-64 transform translate-x-12 -translate-y-12" />
        </div>
        
        <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
          <div className="w-24 h-24 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-4xl font-bold shadow-inner border-2 border-white/30">
            {formData.name.charAt(0).toUpperCase()}
          </div>
          
          <div className="flex-1 text-center md:text-left">
            {isEditing ?
            <div className="flex flex-col gap-2">
                <p className="text-xs font-bold text-white/70 uppercase tracking-widest">{t('profile.fullName')}</p>
                <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-white/10 border border-white/30 rounded-lg px-4 py-2 text-2xl font-bold text-white placeholder-white/50 focus:outline-none focus:bg-white/20 focus:border-white w-full md:w-auto"
                placeholder={t('profile.fullName')}
                autoFocus />

              </div> :

            <h1 className="text-3xl font-bold">{user?.name}</h1>
            }
            
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-3">
              <span className="px-3 py-1 rounded-full text-xs font-bold border bg-white/10 border-white/20 text-white backdrop-blur-sm">
                {getRoleLabel(user?.role)}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-black/20 text-white backdrop-blur-sm">
                {user?.email}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isEditing ?
            <>
                <button
                onClick={handleCancel}
                disabled={saving}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg backdrop-blur-sm transition-colors disabled:opacity-50"
                title="Cancel">

                  <LuX className="w-5 h-5" />
                </button>
                <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-white text-blue-600 font-bold rounded-lg shadow-lg hover:bg-blue-50 transition-colors flex items-center gap-2 disabled:opacity-50">

                  {saving ?
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /> :

                <LuCheck className="w-4 h-4" />
                }
                  {t('profile.saveChanges')}
                </button>
              </> :

            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-lg font-medium backdrop-blur-sm transition-colors flex items-center gap-2">

                <LuPencil className="w-4 h-4" />
                {t('profile.editProfile')}
              </button>
            }
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <LuUser className="w-5 h-5 text-gray-400" />
          {t('profile.accountDetails')}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Read Only Fields */}
          <InfoItem icon={LuMail} label={t('profile.emailAddress')} value={user?.email} />
          <InfoItem icon={LuShield} label={t('profile.systemRole')} value={getRoleLabel(user?.role)} />
          
          {/* Phone Number Field - Editable */}
          {isEditing ?
          <div className="space-y-1">
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                <LuPhone className="w-3 h-3" /> Phone Number
              </label>
              <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-semibold text-gray-900"
              placeholder="Enter phone number" />

            </div> :

          <InfoItem icon={LuPhone} label="Phone Number" value={user?.phone_number || 'N/A'} />
          }

          {/* Editable Fields */}
          {isEditing ?
          <>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <LuBuilding2 className="w-3 h-3" /> {t('profile.property')}
                </label>
                <select
                value={formData.property_id}
                onChange={(e) => setFormData({ ...formData, property_id: e.target.value, building_id: '' })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-semibold text-gray-900">

                  <option value="">{t('profile.selectProperty')}</option>
                  {availableProperties.map((p) =>
                <option key={p.id} value={p.id}>{p.name}</option>
                )}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <LuMapPin className="w-3 h-3" /> {t('profile.building')}
                </label>
                <select
                style={{ color: 'rgb(17, 24, 39)' }}
                value={formData.building_id}
                onChange={(e) => setFormData({ ...formData, building_id: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-semibold text-gray-900"
                disabled={!formData.property_id}>

                  <option value="">{t('profile.selectBuilding')}</option>
                  {availableBuildings.
                filter((b) => b.property_id === formData.property_id).
                map((b) =>
                <option key={b.id} value={b.id}>{b.name}</option>
                )}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <LuBuilding2 className="w-3 h-3" /> {t('profile.unitNumber')}
                </label>
                <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-semibold text-gray-900"
                placeholder={t('profile.unitPlaceholder')} />

              </div>
            </> :

          <>
              <InfoItem icon={LuBuilding2} label={t('profile.property')} value={currentProperty?.name || 'N/A'} />
              <InfoItem icon={LuMapPin} label={t('profile.building')} value={currentBuilding?.name || 'N/A'} />
              <InfoItem icon={LuBuilding2} label={t('profile.unitNumber')} value={user?.unit || 'N/A'} />
            </>
          }

          <InfoItem icon={LuCalendar} label={t('profile.memberSince')} value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'} />
        </div>
      </div>

      {/* Password Change Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <button
          onClick={() => setIsChangingPassword(!isChangingPassword)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">

          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <LuLock className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{t('profile.changePassword')}</h2>
          </div>
          {isChangingPassword ? <LuX className="w-5 h-5 text-gray-400" /> : <LuPencil className="w-4 h-4 text-gray-400" />}
        </button>

        {isChangingPassword &&
        <div className="px-6 py-6 bg-gray-50/50 border-t border-gray-100">
            <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-600">{t('profile.currentPassword')}</label>
                <div className="relative">
                  <input
                  type={showPasswords.current ? "text" : "password"}
                  required
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium pr-10" />

                  <button
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">

                    {showPasswords.current ? <LuEyeOff className="w-4 h-4" /> : <LuEye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-600">{t('profile.newPassword')}</label>
                <div className="relative">
                  <input
                  type={showPasswords.new ? "text" : "password"}
                  required
                  minLength={6}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium pr-10" />

                  <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">

                    {showPasswords.new ? <LuEyeOff className="w-4 h-4" /> : <LuEye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-bold text-gray-600">{t('profile.confirmPassword')}</label>
                <div className="relative">
                  <input
                  type={showPasswords.confirm ? "text" : "password"}
                  required
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all font-medium pr-10" />

                  <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">

                    {showPasswords.confirm ? <LuEyeOff className="w-4 h-4" /> : <LuEye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl shadow-md shadow-blue-200 hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2">

                  {saving ?
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> :
                <LuCheck className="w-4 h-4" />}
                  {t('profile.saveChanges')}
                </button>
                <button
                type="button"
                onClick={() => setIsChangingPassword(false)}
                className="px-6 py-2.5 bg-white border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-all">

                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        }
      </div>

      {/* Account Settings Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-4">{t('profile.accountSettings')}</h2>
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 text-center text-gray-500 text-sm">
          {t('profile.comingSoon')}
        </div>
      </div>
    </div>);

}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="p-2.5 bg-white border border-gray-100 shadow-sm rounded-xl">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <div className="flex-1 overflow-hidden">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="font-semibold text-gray-900 truncate">{value}</p>
      </div>
    </div>);

}